import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useChat, useChatApiClient } from '../../context/index.js';
import {
  detectContentFormat,
  resolveMessageSourceContent,
  extractDocumentDescription,
} from '../../utils/contentUtils.js';
import { useTranslation } from '@aimsos/i18n';
import {
  ResizableSidePanel,
  SearchNavigatorBar,
  SearchableSelect,
  Dropdown,
  DrivePickerModal,
  useToast,
  normalizeConversationStarters,
} from '@aimsos/ui';
import ChatDocumentPanel from './ChatDocumentPanel.jsx';
import ChatSaveToDriveModal from './ChatSaveToDriveModal.jsx';
import {
  downloadDocumentAsPdf,
  isDownloadMenuAction,
} from './chatDocumentDownloadUtils.js';
import { useChatMessageSearch } from './useChatMessageSearch.js';
import { SquarePen, Search, MoreVertical, ExternalLink, History } from 'lucide-react';
import { useAssistantSyncEffects } from './basicChatPanel/useAssistantSyncEffects.js';
import { useAssistantComposer } from './basicChatPanel/useAssistantComposer.js';
import { CHAT_DOCUMENT_SEARCH_DEFAULT_PROPS } from './basicChatPanel/assistantComposerConstants.js';
import { useAssistantDocumentThreadMeta } from './basicChatPanel/useAssistantDocumentThreadMeta.js';
import { useAssistantDocumentEditor } from './basicChatPanel/useAssistantDocumentEditor.js';
import { useAssistantHistoryNav } from './basicChatPanel/useAssistantHistoryNav.jsx';
import AssistantChatBody from './basicChatPanel/AssistantChatBody.jsx';
import { AssistantMainContent } from './basicChatPanel/AssistantMainContent.jsx';
import HitlGateCard from './HitlGateCard.jsx';
import { mapInputRequiredToGateCard } from '../../utils/mapInputRequiredToGateCard.js';
import { useDurableHitlCard } from '../../hooks/useDurableHitlCard.js';
import { lastUserTextBefore } from '../../utils/terminalNotice.js';

// Workflow lifecycle status → `chatPanel.status.*` translation-key suffix.
// Module scope: static, and a stable binding keeps the useMemo deps honest.
const STATUS_LABEL_KEY = {
  QUEUED: 'queued',
  IN_PROGRESS: 'inProgress',
  WRITING: 'writing',
  GENERATING: 'generating',
  WORKING: 'working',
};

/**
 * Shell assistant: `@aimsos/ui` ResizableSidePanel + chat panel states (Empty / Agent / Conversation)
 * over `@aimsos/chat` hooks — top bar aligned with ann global chat (agent SearchableSelect, new chat, in-thread search, menu).
 *
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {'split'|'fullscreen'} [props.layout='split']
 * @param {(next: 'split'|'fullscreen') => void} [props.onLayoutChange]
 * @param {() => void} [props.onOpenInHome] — e.g. navigate to dashboard and close panel; omit to hide menu entry.
 * @param {string} [props.translationNamespace='chat'] — host must call `registerChatI18n(registerNamespace)`.
 * @param {'dock'|'host'} [props.splitResizeSurface='dock'] — `dock`: this component owns the split `Panel` + resize handle (default). `host`: render surface-only (`fill`) so the app can wrap split mode in its own `Panel`/`PanelGroup` without nesting `Panel` inside `Panel`.
 * @param {boolean} [props.embedded=false] — Host-owned surface: no top nav/close, `fill` layout (e.g. agent playground column).
 * @param {string|null} [props.lockAgentId] — When set with `embedded`, selects this agent for the isolated provider tree.
 * @param {object|null} [props.lockAgent] — Agent row used when the agent list has not loaded yet (`uuid`/`id` should match `lockAgentId`).
 * @param {string[]|null} [props.conversationStartersOverride] — Optional starters shown instead of API-derived starters when non-empty.
 * @param {string|null} [props.embeddedHeroTitle] — Overrides the hero title in the agent landing view (embedded playground).
 * @param {string|null} [props.embeddedHeroDescription] — Overrides the hero description in the agent landing view.
 * @param {string|null} [props.composerPlaceholder] — Overrides the default composer placeholder in all chat surfaces.
 * @param {(() => void)|null} [props.onOpenTasks] — Host navigation to the My Tasks inbox; when set, the in-chat HITL gate card shows a "View in My Tasks" link. Omit (embed-safe) to hide it — no hardcoded route in the package.
 */
const BasicChatPanel = ({
  onClose,
  layout = 'split',
  onLayoutChange,
  onOpenInHome,
  translationNamespace = 'chat',
  splitResizeSurface = 'dock',
  embedded = false,
  lockAgentId = null,
  lockAgent = null,
  conversationStartersOverride = null,
  embeddedHeroTitle = null,
  embeddedHeroDescription = null,
  composerPlaceholder = null,
  onOpenTasks = null,
}) => {
  const { t } = useTranslation(translationNamespace);
  const apiClient = useChatApiClient();
  const toast = useToast();
  const isFullscreen = layout === 'fullscreen';
  const splitUsesHostResize = splitResizeSurface === 'host' && !isFullscreen;
  const useFillSurface = isFullscreen || embedded || splitUsesHostResize;

  const chat = useChat();
  const {
    agents,
    isLoadingAgents,
    isLoadingMoreAgents,
    hasMoreAgents,
    selectedAgentId,
    selectedAgent,
    getAgentById,
    selectAgent,
    fetchAgents,
    loadMoreAgents,
    resetAgentsList,
    fetchConversations,
    conversations,
    selectedConversationId,
    selectConversation,
    conversationId,
    fetchMessages,
    messages,
    userId,
    tenantId,
    isLoadingMessages,
    agentIsTyping,
    conversationStarters,
    isStreaming,
    createNewConversation,
    isCreatingConversation,
    defaultAgentForNewChat,
    defaultAgentIdForNewChat,
    isLoadingAgentDetails,
    clearConversationSelection,
    cleanThread,
    openConversationSearch,
    closeConversationSearch,
    isLoadingConversations,
    conversationsError,
    openDocumentPanel,
    closeDocumentPanel,
    isDocumentPanelOpen,
    documentPanelTitle,
    documentPanelContent,
    documentPanelContentFormat,
    currentDocument,
    currentDocumentId,
    updateDocument,
    documentCreatedMessageIdsByConversation,
    sortedDocuments,
    deleteDocument,
    removeDocumentCreatedMessageId,
    hitlRequest,
    setHitlRequest,
    currentActivity,
  } = chat;

  const resolvedInputPlaceholder = composerPlaceholder ?? 'Ask anything…';

  // Workflow state feedback (QUEUED → GENERATING → …) surfaced next to the
  // streaming bar. The hook sets `currentActivity.status`; translate it here,
  // falling back to the hook's English label when no key exists.
  const streamingStatusLabel = useMemo(() => {
    if (!currentActivity) return null;
    const key = STATUS_LABEL_KEY[currentActivity.status];
    const fallback = currentActivity.label || 'Working…';
    return key ? t(`chatPanel.status.${key}`, { defaultValue: fallback }) : fallback;
  }, [currentActivity, t]);

  // Localized copy for the inline run-activity accordion (transient progress
  // steps). The frozen summary uses explicit one/other keys to keep plurals
  // correct across locales without relying on i18next plural suffixes.
  const activityRunningLabel = t('chatPanel.activity.running', { defaultValue: 'Working…' });
  const activityToggleLabel = t('chatPanel.activity.toggle', { defaultValue: 'Show activity steps' });
  const formatActivitySummary = useCallback(
    (count) =>
      count === 1
        ? t('chatPanel.activity.ranStepOne', { count, defaultValue: 'Ran {{count}} step' })
        : t('chatPanel.activity.ranStepOther', { count, defaultValue: 'Ran {{count}} steps' }),
    [t],
  );

  useEffect(() => {
    if (!embedded || !lockAgentId || !selectAgent) return;
    const idStr = String(lockAgentId);
    if (selectedAgentId && String(selectedAgentId) === idStr) return;
    const found = (agents || []).find((a) => String(a.uuid || a.id) === idStr);
    const agentObj =
      found ||
      (lockAgent
        ? {
            ...lockAgent,
            uuid: lockAgent.uuid || lockAgent.id,
            id: lockAgent.id || lockAgent.uuid,
          }
        : null);
    if (!agentObj) return;
    void selectAgent(idStr, agentObj);
  }, [
    embedded,
    lockAgentId,
    lockAgent,
    agents,
    selectedAgentId,
    selectAgent,
  ]);

  useAssistantSyncEffects({
    isFullscreen,
    onLayoutChange,
    selectedAgentId,
    userId,
    fetchConversations,
    conversationId,
    selectedConversationId,
    fetchMessages,
    isLoadingAgents,
    agents,
    defaultAgentForNewChat,
    defaultAgentIdForNewChat,
    selectAgent,
  });

  const messageSearch = useChatMessageSearch(messages);

  const {
    historyOpen,
    handleOpenHistory,
    handleSelectConversationFromHistory,
    historyGroupLabels,
    historyTopNavLeft,
    historyTopNavActions,
    fullscreenBtn,
  } = useAssistantHistoryNav({
    t,
    isFullscreen,
    onLayoutChange,
    messageSearch,
    closeConversationSearch,
    selectConversation,
  });

  const {
    draft,
    setDraft,
    showDrivePickerModal,
    setShowDrivePickerModal,
    drivePickerApi,
    handleDrivePickerConfirm,
    effectiveAgentId,
    handleEmptySend,
    handleAgentSend,
    handleConversationSend,
    chatInputAttachmentProps,
    modelChatProps,
    processing,
    attachFileToPickGate,
    resolvePickGate,
    markWorkflowResuming,
    clearWorkflowResuming,
  } = useAssistantComposer({ chat, toast, t });

  // Terminal-state notice ("no reply" / "failed") copy + Retry. Retry re-sends
  // the user's last prompt before the notice through the regular conversation
  // send path (same composer handler, same attachments/model rules).
  const noticeNoReplyLabel = t('chatPanel.notice.noReply', {
    defaultValue: 'No reply was generated.',
  });
  const noticeFailedLabel = t('chatPanel.notice.failed', {
    defaultValue: 'Something went wrong while generating the reply.',
  });
  const noticeRetryLabel = t('chatPanel.notice.retry', { defaultValue: 'Retry' });
  const handleRetryNotice = useCallback(
    (notice) => {
      const text = lastUserTextBefore(messages, notice);
      if (!text) return;
      void handleConversationSend(text);
    },
    [messages, handleConversationSend],
  );

  const {
    documentEditorResetKey,
    documentAiRewriteEnabled,
    isDocumentAiRewriting,
    handleDocumentAiInstruction,
    handleDocumentContentPayloadChange,
    saveToDriveModalOpen,
    handleOpenSaveToDriveFromEditor,
    handleSaveToDriveModalClose,
    getSaveToDriveDocumentContent,
    handleSaveToDriveDone,
  } = useAssistantDocumentEditor({
    apiClient,
    toast,
    currentDocumentId,
    currentDocument,
    documentPanelContent,
    updateDocument,
  });

  const { documentCreatedMessageIds, documentCardMetaByMessageId } = useAssistantDocumentThreadMeta({
    apiClient,
    messages,
    sortedDocuments,
    documentCreatedMessageIdsByConversation,
    conversationId,
    selectedConversationId,
  });

  const agentOptions = useMemo(
    () =>
      (agents || []).map((a) => {
        const id = a.uuid || a.id;
        const subtitle =
          a.description ||
          a.metadata?.description ||
          a.metadata?.model ||
          a.model ||
          '';
        return {
          id,
          name: a.name || id || 'Agent',
          subtitle: subtitle || undefined,
        };
      }),
    [agents],
  );

  // Trigger-label fallback for the agent picker: the selected agent (e.g. the
  // configured default) may live outside the loaded/paginated options, so give
  // SearchableSelect its option directly to render the trigger label.
  const selectedAgentOption = useMemo(() => {
    if (!selectedAgent) return undefined;
    const id = selectedAgent.uuid || selectedAgent.id;
    if (!id) return undefined;
    const subtitle =
      selectedAgent.description ||
      selectedAgent.metadata?.description ||
      selectedAgent.metadata?.model ||
      selectedAgent.model ||
      '';
    return { id, name: selectedAgent.name || id || 'Agent', subtitle: subtitle || undefined };
  }, [selectedAgent]);

  const handleAgentOptionSelect = useCallback(
    (opt) => {
      const id = opt?.id;
      if (!id) return;
      const agent = agents?.find((a) => (a.uuid || a.id) === id);
      if (!agent) return;
      void selectAgent(id, agent);
      clearConversationSelection();
      cleanThread();
    },
    [agents, selectAgent, clearConversationSelection, cleanThread],
  );

  // Server-side agent search: the list is paginated (pageSize 200), so filtering
  // the already-loaded options client-side can't reach agents beyond the first
  // page. Delegate the SearchableSelect query to the backend via `search`; an
  // empty query refetches the default page.
  const handleAgentSearchChange = useCallback(
    (query) => {
      const search = (query || '').trim();
      void fetchAgents(search ? { search } : {});
    },
    [fetchAgents],
  );

  // Reopening the picker after a search should show the full first page again,
  // not the last search's narrowed results (the picker remounts on selection,
  // so the search box clears but the list would otherwise stay filtered).
  const handleAgentPickerOpen = useCallback(() => {
    void resetAgentsList?.();
  }, [resetAgentsList]);

  const handleNewChat = useCallback(() => {
    if (isDocumentPanelOpen) closeDocumentPanel();
    clearConversationSelection();
    cleanThread();
    messageSearch.handleCloseSearch();
    closeConversationSearch();
  }, [
    isDocumentPanelOpen,
    closeDocumentPanel,
    clearConversationSelection,
    cleanThread,
    messageSearch,
    closeConversationSearch,
  ]);

  const handleOpenInThreadSearch = useCallback(() => {
    openConversationSearch();
    messageSearch.handleOpenSearch();
  }, [openConversationSearch, messageSearch]);

  const handleCloseInThreadSearch = useCallback(() => {
    messageSearch.handleCloseSearch();
    closeConversationSearch();
  }, [messageSearch, closeConversationSearch]);

  const handleOpenMessageAsDocument = useCallback(
    (message) => {
      if (!message) return;
      const sourceContent = resolveMessageSourceContent(message);
      const format = message.contentFormat ?? detectContentFormat(sourceContent);
      const messageId = message.id || message.messageUuid || null;

      openDocumentPanel({
        messageId,
        content: sourceContent,
        format,
      });
    },
    [openDocumentPanel],
  );

  const showEmptyState = !selectedConversationId && !selectedAgentId;
  const showConversationSkeleton = Boolean(selectedConversationId && isLoadingMessages);
  /** Thread ready (may be empty right after first send): show conversation UI, not agent landing. */
  const showConversationThread = Boolean(selectedConversationId && !isLoadingMessages);
  const showAgentView = Boolean(selectedAgentId && !selectedConversationId);

  const isInConversation = Boolean(selectedConversationId);
  const currentConversationId = conversationId || selectedConversationId;

  const threadSearchProps = useMemo(() => {
    if (!messageSearch.isSearchOpen || !isInConversation) return null;
    return {
      searchValue: messageSearch.searchValue,
      currentMatchIndex: messageSearch.currentMatchIndex,
      matches: messageSearch.matches,
      setMessageRef: messageSearch.setMessageRef,
    };
  }, [
    messageSearch.isSearchOpen,
    messageSearch.searchValue,
    messageSearch.currentMatchIndex,
    messageSearch.matches,
    messageSearch.setMessageRef,
    isInConversation,
  ]);

  const agentSelect = useMemo(
    () => (
      <SearchableSelect
        className="min-w-0 max-w-full flex-1"
        options={agentOptions}
        value={selectedAgentId || ''}
        selectedOption={selectedAgentOption}
        onSelect={handleAgentOptionSelect}
        onChange={handleAgentSearchChange}
        onOpen={handleAgentPickerOpen}
        filterLocally={false}
        onLoadMore={loadMoreAgents}
        hasMore={hasMoreAgents}
        isLoadingMore={isLoadingMoreAgents}
        loadingMoreLabel={t('chatPanel.loadingMoreAgents', { defaultValue: 'Loading more…' })}
        isLoading={isLoadingAgents}
        debounceMs={300}
        placeholder={t('chatPanel.searchAgents')}
        searchPlaceholder={t('chatPanel.searchAgents')}
        emptyMessage={t('chatPanel.noAgentsFound')}
        menuListMaxHeight="18rem"
      />
    ),
    [
      agentOptions,
      selectedAgentId,
      selectedAgentOption,
      handleAgentOptionSelect,
      handleAgentSearchChange,
      handleAgentPickerOpen,
      loadMoreAgents,
      hasMoreAgents,
      isLoadingMoreAgents,
      isLoadingAgents,
      t,
    ],
  );

  const topNavLeft = (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      {isLoadingAgentDetails ? (
        <span className="truncate text-sm font-medium text-content-muted">…</span>
      ) : (
        agentSelect
      )}
    </div>
  );

  const moreMenuTrigger = (
    <button
      type="button"
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content"
      aria-label={t('chatPanel.moreOptions')}
      title={t('chatPanel.moreOptions')}
    >
      <MoreVertical size={18} aria-hidden />
    </button>
  );

  const topNavActions = (
    <div className="flex items-center gap-0.5">
      {isInConversation ? (
        <>
          <button
            type="button"
            onClick={handleNewChat}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content"
            aria-label={t('chatPanel.newChat')}
            title={t('chatPanel.newChat')}
          >
            <SquarePen size={18} aria-hidden />
          </button>
          <button
            type="button"
            onClick={handleOpenInThreadSearch}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content"
            aria-label={t('chatPanel.searchInConversation')}
            title={t('chatPanel.searchInConversation')}
          >
            <Search size={18} aria-hidden />
          </button>
        </>
      ) : null}
      <Dropdown trigger={moreMenuTrigger} usePortal alignMenu="right" menuPosition="bottom" width="14rem">
        <Dropdown.Item icon={History} onClick={handleOpenHistory}>
          {t('chatPanel.conversationHistory')}
        </Dropdown.Item>
        {onOpenInHome && isInConversation ? (
          <Dropdown.Item
            icon={ExternalLink}
            onClick={() => {
              onOpenInHome();
            }}
          >
            {t('chatPanel.openInHome')}
          </Dropdown.Item>
        ) : null}
      </Dropdown>
      {fullscreenBtn}
    </div>
  );

  const topNavBelowRow =
    messageSearch.isSearchOpen && isInConversation ? (
      <SearchNavigatorBar
        value={messageSearch.searchValue}
        onChange={messageSearch.handleSearchChange}
        onClear={handleCloseInThreadSearch}
        onClose={handleCloseInThreadSearch}
        currentIndex={messageSearch.currentMatchIndex}
        totalCount={messageSearch.matches.length}
        onPrev={messageSearch.handlePrevMatch}
        onNext={messageSearch.handleNextMatch}
        placeholder={t('chatPanel.searchMessagesPlaceholder')}
        inputAriaLabel={t('chatPanel.searchMessagesPlaceholder')}
        prevAriaLabel={t('chatPanel.prevMatch')}
        nextAriaLabel={t('chatPanel.nextMatch')}
        closeAriaLabel={t('chatPanel.closeSearch')}
      />
    ) : null;

  const chatInputDocuments = useMemo(
    () =>
      (sortedDocuments || []).map((doc) => ({
        id: doc.id,
        messageId: doc.messageId ?? null,
        title: doc.title,
        description: extractDocumentDescription(doc.preview || doc.content || '', 200),
        agentName: doc.agentUuid ? getAgentById(doc.agentUuid)?.name || '' : '',
        driveName: doc.vaultName || '',
        driveUuid: doc.driveUuid || null,
      })),
    [sortedDocuments, getAgentById],
  );

  const documentSearchProps = CHAT_DOCUMENT_SEARCH_DEFAULT_PROPS;

  const handleDocumentSelect = useCallback(
    (doc) => {
      if (!doc?.id) return;
      openDocumentPanel({ documentId: doc.id });
    },
    [openDocumentPanel],
  );

  const handleDocumentDelete = useCallback(
    (doc) => {
      if (!doc?.id) return;
      if (currentConversationId && doc.messageId) {
        removeDocumentCreatedMessageId(currentConversationId, doc.messageId);
      }
      deleteDocument(doc.id);
    },
    [currentConversationId, deleteDocument, removeDocumentCreatedMessageId],
  );

  const handleDocumentSearchMenuChange = useCallback(
    (value, doc) => {
      if (!isDownloadMenuAction(value) || !doc?.id) return;
      const fullDocument = (sortedDocuments || []).find((item) => item.id === doc.id);
      if (!fullDocument) return;

      void downloadDocumentAsPdf({
        title: fullDocument.title,
        content: fullDocument.content,
        contentFormat: fullDocument.contentFormat,
        detectContentFormat,
      });
    },
    [sortedDocuments],
  );

  const starters = useMemo(() => {
    if (embedded && conversationStartersOverride?.length) {
      return normalizeConversationStarters(conversationStartersOverride).slice(0, 8);
    }
    return normalizeConversationStarters(conversationStarters).slice(0, 8);
  }, [embedded, conversationStartersOverride, conversationStarters]);

  const documentPanelNode = (
    <ChatDocumentPanel
      title={currentDocument?.title || documentPanelTitle}
      content={currentDocument?.content ?? documentPanelContent}
      contentFormat={currentDocument?.contentFormat ?? documentPanelContentFormat}
      onContentChange={handleDocumentContentPayloadChange}
      onClose={closeDocumentPanel}
      editorKey={documentEditorResetKey}
      documentAiEnabled={documentAiRewriteEnabled}
      isDocumentAiRewriting={isDocumentAiRewriting}
      onDocumentAiInstruction={documentAiRewriteEnabled ? handleDocumentAiInstruction : undefined}
      onOpenSaveToDrive={handleOpenSaveToDriveFromEditor}
    />
  );

  // HITL gate raised over the WS for the active conversation (input.required).
  const gateCard = useMemo(() => mapInputRequiredToGateCard(hitlRequest), [hitlRequest]);
  const clearGate = useCallback(() => setHitlRequest?.(null), [setHitlRequest]);

  // Document-pick gate (`mode: 'pick'`): open the AIMS Drive picker already
  // wired for attachments, but route its confirm to resolve the gate with the
  // chosen document_uuid instead of attaching. A ref (not state) flags the
  // in-flight picker session so the shared modal's onConfirm can branch.
  const gatePickRef = useRef(null);
  const handlePickDocument = useCallback(
    (card) => {
      gatePickRef.current = card;
      setShowDrivePickerModal(true);
    },
    [setShowDrivePickerModal],
  );

  // Adapter between the card's (card, file) emit and the composer pipeline's
  // (file, card) signature. Stable via useCallback — HitlGateCard is memoized,
  // and an inline arrow would re-render it on every panel render.
  const handleAttachLocalFile = useCallback(
    (card, file) => attachFileToPickGate(file, card),
    [attachFileToPickGate],
  );

  // Surface the card only in a live conversation, docked just above the composer
  // (Cursor/Claude pattern) — not while history or the document panel owns the
  // column. Gate on `currentConversationId` (the active thread the WS matches on),
  // NOT `selectedConversationId`: after a refresh the thread restores as the active
  // conversationId while the UI "selection" stays null until clicked.
  const showGateCard = Boolean(
    gateCard && currentConversationId && !historyOpen && !isDocumentPanelOpen,
  );
  const hitlCardNode = showGateCard ? (
    <HitlGateCard
      key={gateCard.interactionId}
      card={gateCard}
      apiClient={apiClient}
      onResolved={clearGate}
      onResumeStart={markWorkflowResuming}
      onResumeEnd={clearWorkflowResuming}
      onOpenTasks={onOpenTasks}
      onPickDocument={handlePickDocument}
      onAttachLocalFile={handleAttachLocalFile}
      translationNamespace={translationNamespace}
    />
  ) : null;

  const chatBody = (
    <AssistantChatBody
      aboveComposerSlot={hitlCardNode}
      showEmptyState={showEmptyState}
      showConversationSkeleton={showConversationSkeleton}
      showConversationThread={showConversationThread}
      showAgentView={showAgentView}
      draft={draft}
      setDraft={setDraft}
      processing={processing}
      chatInputAttachmentProps={chatInputAttachmentProps}
      modelChatProps={modelChatProps}
      chatInputDocuments={chatInputDocuments}
      documentSearchProps={documentSearchProps}
      handleDocumentSelect={handleDocumentSelect}
      handleDocumentDelete={handleDocumentDelete}
      handleDocumentSearchMenuChange={handleDocumentSearchMenuChange}
      handleEmptySend={handleEmptySend}
      handleConversationSend={handleConversationSend}
      handleAgentSend={handleAgentSend}
      effectiveAgentId={effectiveAgentId}
      isCreatingConversation={isCreatingConversation}
      conversationId={conversationId}
      selectedConversationId={selectedConversationId}
      selectedAgentId={selectedAgentId}
      messages={messages}
      isLoadingMessages={isLoadingMessages}
      agentIsTyping={agentIsTyping}
      isStreaming={isStreaming}
      streamingStatusLabel={streamingStatusLabel}
      activityRunningLabel={activityRunningLabel}
      activityToggleLabel={activityToggleLabel}
      formatActivitySummary={formatActivitySummary}
      noticeNoReplyLabel={noticeNoReplyLabel}
      noticeFailedLabel={noticeFailedLabel}
      noticeRetryLabel={noticeRetryLabel}
      onRetryNotice={handleRetryNotice}
      isFullscreen={isFullscreen}
      threadSearchProps={threadSearchProps}
      handleOpenMessageAsDocument={handleOpenMessageAsDocument}
      documentCreatedMessageIds={documentCreatedMessageIds}
      documentCardMetaByMessageId={documentCardMetaByMessageId}
      starters={starters}
      selectedAgent={selectedAgent}
      isLoadingAgentDetails={isLoadingAgentDetails}
      morePromptsLabel={t('chatPanel.morePrompts')}
      fewerPromptsLabel={t('chatPanel.fewerPrompts')}
      embeddedHeroTitle={embeddedHeroTitle}
      embeddedHeroDescription={embeddedHeroDescription}
      composerPlaceholder={resolvedInputPlaceholder}
    />
  );

  const historyViewProps = {
    conversations,
    selectedConversationId: selectedConversationId || conversationId,
    onSelectConversation: handleSelectConversationFromHistory,
    isLoading: isLoadingConversations,
    searchPlaceholder: t('chatPanel.conversationHistorySearch'),
    emptyLabel: t('chatPanel.noConversations'),
    emptySearchLabel: t('chatPanel.noConversationsFound'),
    loadingLabel: t('chatPanel.loadingConversations'),
    listFetchError: conversationsError,
    listFetchErrorLabel: conversationsError ? t('chatPanel.conversationsListError') : undefined,
    groupLabels: historyGroupLabels,
    fetchConversations,
    agentIdForConversations: effectiveAgentId,
    userId: userId ?? '',
  };

  const mainContent = (
    <AssistantMainContent
      historyOpen={historyOpen}
      historyViewProps={historyViewProps}
      isDocumentPanelOpen={isDocumentPanelOpen}
      isFullscreen={isFullscreen}
      chatBody={chatBody}
      documentPanelNode={documentPanelNode}
    />
  );

  const handleDrivePickerConfirmRouted = useCallback(
    async (selectedDocuments) => {
      const card = gatePickRef.current;
      if (!card) return handleDrivePickerConfirm(selectedDocuments);
      gatePickRef.current = null;
      setShowDrivePickerModal(false);
      const documentUuid = selectedDocuments?.[0]?.document_uuid;
      if (!documentUuid) return undefined;
      await resolvePickGate({ card, documentUuid });
      return undefined;
    },
    [handleDrivePickerConfirm, setShowDrivePickerModal, resolvePickGate],
  );

  // Durable card: survive refresh / conversation switch / missed WS.
  // On conversation change the hook clears the old card then seeds the new
  // one from the task list. Focus and a slow poll reconcile external resolves.
  useDurableHitlCard({
    apiClient,
    conversationId: currentConversationId,
    hitlRequest,
    setHitlRequest,
  });

  return (
    <>
      <ResizableSidePanel
        isOpen
        onClose={embedded ? () => {} : onClose}
        variant={useFillSurface ? 'fill' : 'dock'}
        order={splitUsesHostResize || embedded ? undefined : 2}
        position="right"
        defaultSize={32}
        minSize={22}
        maxSize={50}
        closeOnEscape={!isFullscreen && !embedded}
        showResizeHandle={!embedded && !isFullscreen && !splitUsesHostResize}
        hideCloseState={!embedded}
        topNavCloseAriaLabel={t('chatPanel.closeAssistant')}
        topNavLeftContent={embedded ? null : historyOpen ? historyTopNavLeft : topNavLeft}
        topNavLeftClassName="min-w-0 flex-1"
        topNavActionsContent={embedded ? null : historyOpen ? historyTopNavActions : topNavActions}
        topNavBelowRow={embedded || historyOpen ? null : topNavBelowRow}
        lightBgClassName="bg-surface"
        darkBgClassName="dark:bg-surface"
        panelBodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden !bg-surface dark:!bg-surface"
        panelAriaLabel={embedded ? 'Agent playground chat' : 'Personal assistant chat'}
      >
        <div
          className={[
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            embedded ? 'p-4 sm:p-5 md:p-6' : 'px-3 pb-2 pt-1',
          ].join(' ')}
        >
          {mainContent}
        </div>
        <ChatSaveToDriveModal
          isOpen={saveToDriveModalOpen}
          onClose={handleSaveToDriveModalClose}
          documentTitle={currentDocument?.title || documentPanelTitle || 'Document'}
          apiClient={apiClient}
          userId={userId}
          tenantId={tenantId}
          getDocumentContent={getSaveToDriveDocumentContent}
          onSaved={handleSaveToDriveDone}
        />
      </ResizableSidePanel>
      <DrivePickerModal
        isOpen={showDrivePickerModal}
        onClose={() => setShowDrivePickerModal(false)}
        onConfirm={handleDrivePickerConfirmRouted}
        drives={drivePickerApi.drives}
        isLoadingDrives={drivePickerApi.isLoadingDrives}
        fetchDriveRoot={drivePickerApi.fetchDriveRoot}
        fetchFolderChildren={drivePickerApi.fetchFolderChildren}
        useBodyPortal
        title={t('drivePicker.title')}
        cancelLabel={t('drivePicker.cancel')}
        addSelectedLabel={t('drivePicker.addSelected')}
        processingLabel={t('drivePicker.processing')}
        loadingLabel={t('drivePicker.loading')}
        emptyDrivesMessage={t('drivePicker.emptyDrives')}
        emptyDriveMessage={t('drivePicker.emptyDrive')}
        emptyFolderMessage={t('drivePicker.emptyFolder')}
        closeAriaLabel={t('drivePicker.closeAria')}
      />
    </>
  );
};

export default BasicChatPanel;
